import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { Person } from '@feathr/blackbox';
import {
  CardV2 as Card,
  Checkbox,
  List,
  Pill,
  SaveButtonValid,
  Spinner,
  Text,
  Toggle,
} from '@feathr/components';
import { useActionBar, useStore } from '@feathr/extender/state';
import { flattenErrors } from '@feathr/hooks';
import type { TValidateGrouped } from '@feathr/rachis';

import { useSubscriptionsFilter } from './Subscriptions.hooks';
import {
  filterProjects,
  getProjectCountText,
  hasEmailHistory,
  sortProjects,
} from './Subscriptions.utils';

import * as styles from './Subscriptions.css';

interface IProps {
  readonly person: Person;
}

function validatePersonSubscriptions(person: Person): TValidateGrouped {
  return person.validate(['email'], false, 'grouped').errors;
}

function Subscriptions({ person }: IProps): JSX.Element {
  const { t } = useTranslation();
  const { Events: Projects } = useStore();
  const { setRightActions } = useActionBar();
  const subscriptions = person.get('pp_opt_outs', { all: false, events: [] });

  const validationErrors = validatePersonSubscriptions(person);

  const {
    showOnlyEmailedProjects,
    projectIdsWithEmailHistory,
    isLoadingEmailHistory,
    toggleFilter,
  } = useSubscriptionsFilter(person.id);

  const projects = Projects.list({
    filters: {
      is_archived__ne: true,
    },
    only: ['id', 'name', 'email_preferences_alias'],
    ordering: ['name'],
    pagination: {
      page_size: 1000,
    },
  });

  useEffect(() => {
    setRightActions(
      <SaveButtonValid
        disabled={!person.isDirty}
        errors={flattenErrors(validationErrors)}
        method={'patch'}
        model={person}
      >
        {t('Save')}
      </SaveButtonValid>,
    );
  }, [person.isDirty]);

  function handleChangeAll(isOptedOut?: boolean): void {
    person.set({
      pp_opt_outs: { ...subscriptions, all: Boolean(isOptedOut) },
    });
  }

  function handleChangeEvent(eventId: string): (newValue?: boolean) => void {
    return function (newValue?: boolean) {
      let newEvents: string[] = toJS(subscriptions.events) ?? [];
      if (!newValue) {
        if (!newEvents.includes(eventId)) {
          newEvents.push(eventId);
        }
      } else {
        newEvents = newEvents.filter((id) => id !== eventId);
      }

      person.set({ pp_opt_outs: { ...subscriptions, events: newEvents } });
    };
  }

  const isLoading = projects.isPending || isLoadingEmailHistory;
  const sortedProjects = sortProjects(projects.models);
  const filteredProjects = filterProjects(
    sortedProjects,
    showOnlyEmailedProjects,
    projectIdsWithEmailHistory,
  );
  const hasNoEmailHistory = showOnlyEmailedProjects && filteredProjects.length === 0 && !isLoading;

  return (
    <div className={styles.root}>
      <Card>
        <Card.Header title={t('Subscriptions')} />
        <Card.Content addVerticalGap={true}>
          <Toggle
            helpText={t('Show only projects this person has received at least one email from.')}
            label={t('Show only projects with email history')}
            layout={'well'}
            onChange={toggleFilter}
            value={showOnlyEmailedProjects}
          />

          {!isLoading && (
            <Text className={styles.projectCount} variant={'smallRegular'}>
              {getProjectCountText(t, filteredProjects.length, sortedProjects.length)}
            </Text>
          )}

          <Checkbox
            className={styles.checkbox}
            label={
              <Text.DefaultHeadingMedium>
                {t('Unsubscribe from all {{count}} projects', { count: sortedProjects.length })}
              </Text.DefaultHeadingMedium>
            }
            layout={'well'}
            onChange={handleChangeAll}
            value={subscriptions.all}
          />

          {hasNoEmailHistory ? (
            <Text className={styles.noEmailsMessage} variant={'smallRegular'}>
              {t('This person has not received any emails yet.')}
            </Text>
          ) : (
            <List className={styles.list}>
              {isLoading ? (
                <Spinner />
              ) : (
                filteredProjects.map((project) => {
                  const alias = project.get('email_preferences_alias');
                  const name = project.get('name');
                  const isSubscribed = !subscriptions.events?.includes(project.id);
                  const projectHasEmailHistory = hasEmailHistory(
                    project.id,
                    projectIdsWithEmailHistory,
                  );
                  const showReceivedIndicator = !showOnlyEmailedProjects && projectHasEmailHistory;

                  return (
                    <List.Item key={project.id}>
                      <Checkbox
                        className={styles.checkbox}
                        disabled={subscriptions.all}
                        label={
                          <>
                            {alias ? `${alias} (${name})` : name}
                            {showReceivedIndicator && (
                              <Pill
                                className={styles.emailIndicator}
                                theme={'sky'}
                                variant={'secondary'}
                              >
                                {t('Received')}
                              </Pill>
                            )}
                          </>
                        }
                        onChange={handleChangeEvent(project.id)}
                        value={isSubscribed}
                      />
                    </List.Item>
                  );
                })
              )}
            </List>
          )}
        </Card.Content>
      </Card>
    </div>
  );
}

export default observer(Subscriptions);
